import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { wrappingTask } from '../../../lib/taskrouter';
import { muteConnection, disconnect } from '../../../lib/device';
import {
  Button as BaseButton,
  Recording,
  VolumeControl,
  Typography,
  SessionIdleModal
} from '../../../components';
import {
  startCall as startCallAction,
  stopRecording as stopRecordingAction,
  stopCall as stopCallAction,
  startRecording as startRecordingAction
} from '../../../lib/redux';
import {
  RECORDING_STATUS,
  ROUTES,
  NO_CALL_ONGOING,
  BROADBAND_CALL_CODE,
  CALL_STATUS,
  COMPLETE_TASK_RESULT
} from '../../../Constants';
import { colors } from '../../../utils';

const Padded = styled.div`
  margin-bottom: 20px;
  width: 90%;
`;

const InfoText = styled(Typography)`
  color: ${colors.g04};
  font-size: 12px;
  margin-bottom: 10px;
`;

const Button = styled(BaseButton)`
  flex: 1;
  margin-right: 15px;

  &:last-child {
    margin-right: 0;
  }
`;

const Controls = ({
  task,
  startRecording,
  stopRecording,
  history,
  startCall,
  setError,
  setRecordingError,
  call,
  setIsRunning
}) => {
  const [falseNarrowbandHangup, setFalseNarrowbandHangup] = useState(false);
  const [showSessionIdleModal, setShowSessionIdleModal] = useState(false);
  const [hasRecordedOnce, setHasRecorded] = useState(false);
  const [redialCallLoading, setRedialCallLoading] = useState(false);

  const toggleRecording = async () => {
    if (call.recordingStatus === null || call.recordingStatus === RECORDING_STATUS.COMPLETED) {
      try {
        await startRecording(task.taskId, call.callId);
      } catch (err) {
        if (err.body === NO_CALL_ONGOING) {
          setError(NO_CALL_ONGOING);
        } else {
          setError(true);
        }

        return Promise.reject();
      }

      setRecordingError(false);
      setIsRunning(true);

      return Promise.resolve();
    }

    if (call.recordingStatus === RECORDING_STATUS.INPROGRESS) {
      try {
        await stopRecording(task.taskId, call.callId);
      } catch (err) {
        setIsRunning(false);
        setHasRecorded(true);
        if (err.body === NO_CALL_ONGOING) {
          setRecordingError(NO_CALL_ONGOING);
        } else {
          setRecordingError(true);
        }
      }

      setIsRunning(false);
      setHasRecorded(true);

      return Promise.resolve();
    }

    return Promise.resolve();
  };

  const redialCall = useCallback(async () => {
    setFalseNarrowbandHangup(false);

    setRedialCallLoading(true);
    try {
      await startCall(task.taskId, task.jobTypeCode);
    } catch (err) {
      if (get(err, 'body.resultType') === COMPLETE_TASK_RESULT.ALREADY_SUBMITTED) {
        setShowSessionIdleModal(true);
      } else {
        setError(true);
      }
    }
    setRedialCallLoading(false);
  });

  const muteCall = isMuted => {
    try {
      muteConnection(isMuted);
    } catch (err) {
      setError(true);
    }
  };

  const hangupCall = useCallback(() => {
    if (task.jobTypeCode === BROADBAND_CALL_CODE) {
      try {
        disconnect();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    } else {
      setFalseNarrowbandHangup(true);
    }
  });

  const evaluateCall = useCallback(async () => {
    try {
      await wrappingTask(task.taskId);
      history.push(ROUTES.JOB_FEEDBACK);
    } catch (err) {
      if (err.code === 404) {
        // TODO: we make an assumption here from the Twilio not found response
        setShowSessionIdleModal(true);
      } else {
        setError(true);
      }
    }
  });

  return (
    <>
      {call.recordingStatus !== RECORDING_STATUS.COMPLETED &&
        task.jobTypeCode === BROADBAND_CALL_CODE && <VolumeControl onMuteClick={muteCall} />}

      {call.callStatus <= CALL_STATUS.INPROGRESS.code && !falseNarrowbandHangup && (
        <Recording onClick={toggleRecording} call={call} />
      )}

      {call.callStatus === CALL_STATUS.INPROGRESS.code && !falseNarrowbandHangup && (
        <Padded>
          <Button
            theme={call.recordingStatus !== null && !hasRecordedOnce ? 'bordered' : 'default'}
            disabled={call.recordingStatus === RECORDING_STATUS.INPROGRESS}
            onClick={hangupCall}
          >
            Hang up
          </Button>
        </Padded>
      )}

      {(call.callStatus > CALL_STATUS.INPROGRESS.code || falseNarrowbandHangup) && (
        <>
          {call.callStatus === CALL_STATUS.BUSY.code && (
            <InfoText>Call unsuccessful - the user is busy.</InfoText>
          )}
          {call.callStatus === CALL_STATUS.FAILED.code && (
            <InfoText>Call unsuccessful - the call failed to connect.</InfoText>
          )}
          {call.callStatus === CALL_STATUS.NOANSWER.code && (
            <InfoText>Call unsuccessful - the user did not pick up the phone.</InfoText>
          )}
          {call.callStatus === CALL_STATUS.CANCELED.code && (
            <InfoText>Call unsuccessful - the call was cancelled.</InfoText>
          )}

          <Padded style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button theme="bordered" disabled={redialCallLoading} onClick={redialCall}>
              Re-dial
            </Button>

            <Button onClick={evaluateCall}>Evaluate call</Button>
          </Padded>
        </>
      )}
      {showSessionIdleModal && <SessionIdleModal history={history} />}
    </>
  );
};

Controls.propTypes = {
  startCall: PropTypes.func.isRequired,
  startRecording: PropTypes.func.isRequired,
  stopRecording: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired,
  task: PropTypes.shape({
    jobCall: PropTypes.string,
    taskId: PropTypes.string,
    taskInput: PropTypes.string
  }),
  setError: PropTypes.func.isRequired,
  setRecordingError: PropTypes.func.isRequired,
  setIsRunning: PropTypes.func.isRequired,
  call: PropTypes.shape({
    callStatus: PropTypes.number,
    recordingStatus: PropTypes.number,
    callId: PropTypes.string
  }).isRequired
};

Controls.defaultProps = {
  task: {}
};

export default connect(
  ({ tasks, call }) => ({
    task: {
      ...tasks.currentTask.task
    },
    call
  }),
  {
    startCall: startCallAction,
    stopCall: stopCallAction,
    startRecording: startRecordingAction,
    stopRecording: stopRecordingAction
  }
)(withRouter(Controls));
